import { useMutation } from '@tanstack/react-query';
import { gsap } from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Text } from '@column/column-ui-kit';

import { ROUTE as APP_ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { MFAStatus } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Headline } from '~/styles';
import { log } from '~/util';

import { Logo, Panel, animateLogo } from './_components';

const AdditionalLogo = styled(Logo)`
  margin-left: -12px;
  transform-origin: 0 0;
  overflow: visible;

  & > path {
    --logo-path-opacity: 0;
    --logo-path-x: 16px;
    --logo-path-y: 24px;
    opacity: var(--logo-path-opacity);
    transform: translate(var(--logo-path-x), var(--logo-path-y)) translateZ(0);
  }
`;

const HiddenLogo = styled(Logo)`
  opacity: 0;
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
`;

interface Params {
  code: string;
}

export const PageVerifyEmail: React.FC = () => {
  const { currentUser, mfaStatus } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { code } = useParams<keyof Params>() as Params;

  const verifyEmailMutation = useMutation({
    mutationFn: async () => currentUser?.verifyEmail(code),
    onSuccess: () => {
      log({
        name: 'User email verified',
        context: {
          email: currentUser?.email,
        },
      });
    },
    onError: (error: any) => {
      if (error?.message) {
        addDangerNotification({
          content: error.message,
          display: 'page-verify',
        });
      }

      log({
        name: 'User email verification failed',
        context: {
          error,
        },
      });
    },
    retry: false,
  });

  useEffect(() => {
    if (code && currentUser && verifyEmailMutation.isIdle) {
      verifyEmailMutation.mutate();
    }
  }, [code, currentUser, verifyEmailMutation]);

  const handleRedirectToDashboard = () => {
    if (mfaStatus !== MFAStatus.VERIFIED) {
      navigate(PUBLIC_ROUTE.SETUP_MFA);
      return;
    }

    if (!logoRef.current || !logoWrapperRef.current || !panelRef.current) {
      navigate(APP_ROUTE.ROOT, {
        state: { from: 'auth' },
      });
      return;
    }

    // Lock the panel dimensions to prevent layout shifts during animation
    const panel = panelRef.current;
    const panelRect = panel.getBoundingClientRect();
    panel.style.width = `${panelRect.width}px`;
    panel.style.height = `${panelRect.height}px`;

    animateLogo(logoRef.current, logoWrapperRef.current, panelRef.current, gsap, MotionPathPlugin, () => {
      navigate(APP_ROUTE.ROOT, {
        state: { from: 'auth' },
      });
    });
  };

  const errorMessage = verifyEmailMutation.error
    ? verifyEmailMutation.error?.message
    : 'Please try again or request a new verification email.';

  return (
    <>
      <LogoWrapper ref={logoWrapperRef}>
        <AdditionalLogo size="24px" variant="full" ref={logoRef} />
      </LogoWrapper>
      <Panel ref={panelRef}>
        <HiddenLogo size="24px" />
        <Loading minHeight={false} isLoading={verifyEmailMutation.isIdle || verifyEmailMutation.isPending}>
          <Flex flexDirection="column" gap={8} mb={8}>
            <Headline fullWidth size="small">
              {verifyEmailMutation.isSuccess ? 'Verified!' : 'Verification failed.'}
            </Headline>
            <Text lineHeight="body" size={14} color="foreground" weight="normal">
              {verifyEmailMutation.isSuccess ? 'Email address successfully verified.' : errorMessage}
            </Text>
          </Flex>
        </Loading>
        <Button onClick={handleRedirectToDashboard}>Continue</Button>
      </Panel>
    </>
  );
};
